import { router } from '@inertiajs/react'
import { type ReactNode, useState } from 'react'
import { Switch } from '@/components/ui/switch'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { AlertTriangle } from 'lucide-react'
import type { InstancePlugins } from '../types'

interface Props {
  worker: { id: string }
  plugins: InstancePlugins | null
}

type Tone = 'success' | 'warning' | 'neutral'

const PILL_CLASSES: Record<Tone, string> = {
  success: 'bg-green-100 text-green-700 dark:bg-green-500/15 dark:text-green-400',
  warning: 'bg-amber-100 text-amber-700 dark:bg-yellow-500/15 dark:text-yellow-400',
  neutral: 'bg-accent text-muted-foreground',
}

function Pill({ tone, children }: { tone: Tone; children: ReactNode }) {
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${PILL_CLASSES[tone]}`}>
      {children}
    </span>
  )
}

export default function PluginsTab({ worker, plugins }: Props) {
  const [pending, setPending] = useState<string | null>(null)
  const [confirmation, setConfirmation] = useState<{ pluginId: string; desired: boolean } | null>(null)

  if (!plugins) {
    return (
      <div className="bg-card border border-border rounded-lg p-8 text-center text-sm text-muted-foreground">
        Não foi possível ler os plugins do broker.
      </div>
    )
  }

  const toggle = (pluginId: string, current: boolean | null) => {
    const desired = !(current === true)
    setConfirmation({ pluginId, desired })
  }

  const executeToggle = (pluginId: string, desired: boolean) => {
    setPending(pluginId)
    router.post(
      `/cc/instances/${worker.id}/toggle_plugin`,
      { plugin_id: pluginId, enabled: desired },
      { preserveScroll: true, onFinish: () => setPending(null) },
    )
  }

  const formatInstall = (install: unknown) => {
    if (install === null || install === undefined || install === '') return '—'
    if (typeof install === 'string') return install
    return JSON.stringify(install)
  }

  return (
    <div className="space-y-4">
      <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-500/20 rounded-lg px-4 py-3 flex items-start gap-3">
        <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
        <div className="text-sm text-amber-900 dark:text-amber-100">
          Mudanças exigem restart manual do container. Esta página não restarta nada automaticamente.
        </div>
      </div>

      <div className="flex items-center gap-4 text-sm text-muted-foreground">
        <span>Total <strong className="text-foreground">{plugins.counts.total}</strong></span>
        <span>·</span>
        <span>Ativos <strong className="text-emerald-600 dark:text-emerald-400">{plugins.counts.enabled}</strong></span>
        <span>·</span>
        <span>Inativos <strong className="text-amber-600 dark:text-amber-400">{plugins.counts.disabled}</strong></span>
      </div>

      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <table className="w-full">
          <thead className="bg-muted border-b border-border">
            <tr>
              <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">Plugin</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">Versão</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">Origem</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">Install</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">Status</th>
              <th className="text-right px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">Toggle</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {plugins.items.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-sm text-muted-foreground">
                  Nenhum plugin declarado em <code className="font-mono">openclaw.json</code>.
                </td>
              </tr>
            )}
            {plugins.items.map((p) => (
              <tr key={p.id} className="hover:bg-accent transition-colors">
                <td className="px-4 py-3 font-mono text-xs text-foreground">{p.id}</td>
                <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{p.version || '—'}</td>
                <td className="px-4 py-3 text-xs text-muted-foreground">{p.source}</td>
                <td className="px-4 py-3 font-mono text-xs text-muted-foreground break-all">{formatInstall(p.install)}</td>
                <td className="px-4 py-3">
                  {p.enabled === true && <Pill tone="success">Ativo</Pill>}
                  {p.enabled === false && <Pill tone="warning">Inativo</Pill>}
                  {p.enabled === null && <span className="text-xs text-muted-foreground">—</span>}
                </td>
                <td className="px-4 py-3 text-right">
                  <Switch
                    checked={p.enabled === true}
                    disabled={pending !== null}
                    onCheckedChange={() => toggle(p.id, p.enabled)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {plugins.load_paths.length > 0 && (
        <details className="bg-card border border-border rounded-lg overflow-hidden">
          <summary className="cursor-pointer px-4 py-3 bg-muted border-b border-border text-sm font-semibold">
            Load paths ({plugins.load_paths.length})
          </summary>
          <ul className="px-4 py-3 text-xs font-mono space-y-1 text-muted-foreground">
            {plugins.load_paths.map((p) => <li key={p}>{p}</li>)}
          </ul>
        </details>
      )}
      <AlertDialog open={confirmation !== null} onOpenChange={(open) => { if (!open) setConfirmation(null) }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{confirmation?.desired ? 'Habilitar plugin' : 'Desabilitar plugin'}</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmation?.desired ? 'Habilitar' : 'Desabilitar'} plugin "{confirmation?.pluginId}"? Reinicie o container manualmente para aplicar.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setConfirmation(null)}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                const next = confirmation
                setConfirmation(null)
                if (next) executeToggle(next.pluginId, next.desired)
              }}
            >
              {confirmation?.desired ? 'Habilitar' : 'Desabilitar'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
